import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const DISMISS_KEY = "nutriscan_pwa_dismissed";

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallPWA = () => {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionStorage.getItem(DISMISS_KEY) === "1") return;
    // iOS Safari не поддерживает beforeinstallprompt — показываем инструкцию
    if (isIOS()) { setIos(true); setVisible(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setVisible(false));
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (ios) { setShowIosGuide(true); return; }
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div data-testid="pwa-install-banner"
        className="fixed inset-x-3 bottom-3 z-[100] md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4">
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-white/95 backdrop-blur p-3 shadow-[0_12px_30px_rgb(4,120,87,0.18)]">
          <img src="/logo-mark.png" alt="NutriScan" className="w-11 h-11 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900">{t("pwa.title")}</p>
            <p className="text-xs text-muted-foreground">{ios ? t("pwa.iosBanner") : t("pwa.subtitle")}</p>
          </div>
          <button onClick={install} data-testid="pwa-install-button"
            className="flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 h-9 transition-colors">
            {ios ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            {ios ? t("pwa.howto") : t("pwa.install")}
          </button>
          <button onClick={dismiss} data-testid="pwa-dismiss-button" className="text-slate-400 hover:text-slate-600 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showIosGuide && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/40 p-4"
          onClick={() => setShowIosGuide(false)} data-testid="pwa-ios-guide">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-xl animate-in slide-in-from-bottom-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <img src="/logo-mark.png" alt="NutriScan" className="w-12 h-12 rounded-2xl" />
              <h3 className="font-heading font-bold text-lg text-slate-900">{t("pwa.iosTitle")}</h3>
            </div>
            <ol className="space-y-4">
              {[
                { n: 1, icon: <Share className="w-5 h-5 text-emerald-600" />, text: t("pwa.iosStep1") },
                { n: 2, icon: <Plus className="w-5 h-5 text-emerald-600" />, text: t("pwa.iosStep2") },
                { n: 3, icon: <Download className="w-5 h-5 text-emerald-600" />, text: t("pwa.iosStep3") },
              ].map((s) => (
                <li key={s.n} className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700 shrink-0">{s.n}</span>
                  <span className="flex-1 text-sm text-slate-700">{s.text}</span>
                  {s.icon}
                </li>
              ))}
            </ol>
            <button onClick={() => { setShowIosGuide(false); dismiss(); }} data-testid="pwa-ios-gotit"
              className="mt-6 w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors">
              {t("pwa.gotIt")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
